import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Megaphone, Gavel, BadgeIndianRupee, Trophy, Landmark, ListChecks, AlertTriangle, ArrowUpRight,
} from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import { STATUS_BADGE, formatCurrency } from "@/lib/constants";
import { KpiTile, TasksTable, DashHeader, MomStrip } from "@/components/DashboardKit";

const TH = "text-left py-2.5 px-3 text-[11px] font-semibold uppercase tracking-wider text-slate-600";

function OpenCell({ module, id }) {
  return (
    <td className="py-2.5 px-3 text-right">
      <Link to={`/m/${module}/${id}`} target="_blank" rel="noreferrer" className="text-blue-600 inline-flex items-center" data-testid={`tender-open-${id}`}>
        Open <ArrowUpRight className="h-3.5 w-3.5 ml-0.5" />
      </Link>
    </td>
  );
}

function TenderTable({ cols, rows, empty, testid, children }) {
  return (
    <Card className="border-slate-200"><CardContent className="p-5">
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 border-b border-slate-200"><tr>
            {cols.map((c) => <th key={c} className={TH}>{c}</th>)}
            <th className="py-2.5 px-3 text-right text-[11px] font-semibold uppercase tracking-wider text-slate-600">Open</th>
          </tr></thead>
          <tbody data-testid={testid}>
            {rows.length === 0 && <tr><td colSpan={cols.length + 1} className="text-center py-10 text-slate-500">{empty}</td></tr>}
            {rows.map(children)}
          </tbody>
        </table>
      </div>
    </CardContent></Card>
  );
}

export default function TenderDashboardPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notified, setNotified] = useState(() => new Set());
  const { user } = useAuth();

  useEffect(() => {
    let mounted = true;
    api.get("/dashboard/tender")
      .then((r) => mounted && setData(r.data))
      .catch(() => toast.error("Failed to load tender dashboard"))
      .finally(() => mounted && setLoading(false));
    return () => { mounted = false; };
  }, []);

  useEffect(() => {
    if (!data) return;
    const batch = [];
    for (const t of data.tasks || []) if (t.overdue) batch.push({ k: `t-${t.id}`, m: `Overdue task: ${t.task_title}` });
    for (const x of data.new_tenders || []) if (x.due_soon) batch.push({ k: `tn-${x.id}`, m: `Tender closing soon: ${x.tender_title}` });
    for (const e of data.emd || []) if ((e.emd_status || "") !== "Refunded") batch.push({ k: `emd-${e.id}`, m: `EMD refund pending: ${e.tender_title}` });
    const fresh = batch.filter((b) => !notified.has(b.k)).slice(0, 5);
    if (fresh.length) {
      fresh.forEach((n) => toast.warning(n.m, { icon: "⚠️" }));
      setNotified((prev) => { const n = new Set(prev); fresh.forEach((f) => n.add(f.k)); return n; });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  if (loading) return <div className="p-6 space-y-4"><Skeleton className="h-10 w-80" /><div className="grid grid-cols-4 gap-3">{[1,2,3,4].map(i=><Skeleton key={i} className="h-24" />)}</div><Skeleton className="h-80" /></div>;
  if (!data) return null;
  const s = data.summary || {};

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-6" data-testid="tender-dashboard-page">
      <DashHeader
        eyebrow="Tender Dashboard"
        title={`Hello ${user?.name?.split(" ")[0]} — tender pipeline`}
        hint="New → Bidding → Quoted → Won, with EMD refund tracking and assigned tasks."
        right={<div className="flex gap-2">
          {s.closing_soon > 0 && <Badge variant="outline" className="border-amber-200 text-amber-700 bg-amber-50 gap-1"><AlertTriangle className="h-3 w-3" />{s.closing_soon} closing soon</Badge>}
          {s.emd_refund_due > 0 && <Badge variant="outline" className="border-blue-200 text-blue-700 bg-blue-50 gap-1"><BadgeIndianRupee className="h-3 w-3" />{formatCurrency(s.emd_refund_due)} EMD pending</Badge>}
        </div>}
      />

      <MomStrip items={data.mom} />

      <div className="grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="kpi-grid">
        <KpiTile label="New Tenders" value={s.new_tenders} sub={`${s.closing_soon} closing ≤7d`} icon={Megaphone} accent="blue" testid="kpi-new-tenders" />
        <KpiTile label="Bidding" value={s.bidding} sub="decided to bid" icon={Gavel} accent="amber" testid="kpi-bidding" />
        <KpiTile label="Quoted" value={s.quoted} sub="bid submitted" icon={BadgeIndianRupee} accent="violet" testid="kpi-quoted" />
        <KpiTile label="Won" value={s.won} sub={formatCurrency(s.won_value)} icon={Trophy} accent="emerald" testid="kpi-won" />
        <KpiTile label="EMD Pending" value={formatCurrency(s.emd_refund_due)} sub={`${s.emd_count} tenders`} icon={Landmark} accent="rose" testid="kpi-emd" />
      </div>

      <Tabs defaultValue="new" data-testid="tender-tabs">
        <TabsList className="h-auto flex-wrap bg-slate-100 p-1">
          <TabsTrigger value="new" data-testid="tab-new-tenders"><Megaphone className="h-3.5 w-3.5 mr-1.5" /> New Tenders</TabsTrigger>
          <TabsTrigger value="bidding" data-testid="tab-bidding"><Gavel className="h-3.5 w-3.5 mr-1.5" /> Bidding Progress</TabsTrigger>
          <TabsTrigger value="quoted" data-testid="tab-quoted"><BadgeIndianRupee className="h-3.5 w-3.5 mr-1.5" /> Quoted Tenders</TabsTrigger>
          <TabsTrigger value="won" data-testid="tab-won"><Trophy className="h-3.5 w-3.5 mr-1.5" /> Tenders Won</TabsTrigger>
          <TabsTrigger value="emd" data-testid="tab-emd"><Landmark className="h-3.5 w-3.5 mr-1.5" /> EMD Tracking</TabsTrigger>
          <TabsTrigger value="tasks" data-testid="tab-tasks"><ListChecks className="h-3.5 w-3.5 mr-1.5" /> Pending Tasks</TabsTrigger>
        </TabsList>

        {/* New Tenders */}
        <TabsContent value="new">
          <TenderTable testid="new-tenders-table" rows={data.new_tenders || []} empty="No active tenders. Past-closing tenders auto-drop once their due date passes."
            cols={["Tender", "Dept", "State", "Closing", "Value", "EMD", "Assigned"]}>
            {(t) => (
              <tr key={t.id} className={`border-b border-slate-100 hover:bg-slate-50/60 ${t.due_soon ? "bg-amber-50/40" : ""}`} data-testid={`new-tender-row-${t.id}`}>
                <td className="py-2.5 px-3 font-semibold text-slate-900">{t.tender_title || "—"}<div className="text-[11px] text-slate-400 font-mono">{t.tender_id || ""}</div></td>
                <td className="py-2.5 px-3 text-slate-700">{t.department || "—"}</td>
                <td className="py-2.5 px-3 text-slate-600">{t.state || "—"}</td>
                <td className="py-2.5 px-3"><span className={t.due_soon ? "text-amber-700 font-semibold" : "text-slate-700"}>{t.closing_date || "—"}{t.due_soon && " · SOON"}</span></td>
                <td className="py-2.5 px-3 font-mono font-semibold">{formatCurrency(t.tender_value)}</td>
                <td className="py-2.5 px-3 font-mono">{formatCurrency(t.emd)}</td>
                <td className="py-2.5 px-3 text-slate-700">{t.assigned_to || "—"}</td>
                <OpenCell module="tenders_published" id={t.id} />
              </tr>
            )}
          </TenderTable>
        </TabsContent>

        {/* Bidding Progress */}
        <TabsContent value="bidding">
          <TenderTable testid="bidding-table" rows={data.bidding || []} empty="No tenders in bidding. Set a tender's status to 'Will Bid' to move it here."
            cols={["Tender", "Closing", "Docs", "EMD", "Tech Bid", "Fin Bid", "Submission", "EMD Amt"]}>
            {(t) => (
              <tr key={t.id} className={`border-b border-slate-100 hover:bg-slate-50/60 ${t.overdue ? "bg-red-50/40" : ""}`} data-testid={`bid-row-${t.id}`}>
                <td className="py-2.5 px-3 font-semibold text-slate-900">{t.tender_title || "—"}<div className="text-[11px] text-slate-400 font-mono">{t.tender_id || ""}</div></td>
                <td className="py-2.5 px-3"><span className={t.overdue ? "text-red-600 font-semibold" : "text-slate-700"}>{t.closing_date || "—"}</span></td>
                <td className="py-2.5 px-3"><Badge variant="outline" className={STATUS_BADGE(t.doc_prep_status || "")}>{t.doc_prep_status || "—"}</Badge></td>
                <td className="py-2.5 px-3"><Badge variant="outline" className={STATUS_BADGE(t.emd_status || "")}>{t.emd_status || "—"}</Badge></td>
                <td className="py-2.5 px-3"><Badge variant="outline" className={STATUS_BADGE(t.tech_bid_status || "")}>{t.tech_bid_status || "—"}</Badge></td>
                <td className="py-2.5 px-3"><Badge variant="outline" className={STATUS_BADGE(t.fin_bid_status || "")}>{t.fin_bid_status || "—"}</Badge></td>
                <td className="py-2.5 px-3"><Badge variant="outline" className={STATUS_BADGE(t.submission_status || "")}>{t.submission_status || "—"}</Badge></td>
                <td className="py-2.5 px-3 font-mono">{formatCurrency(t.emd)}</td>
                <OpenCell module="tenders_published" id={t.id} />
              </tr>
            )}
          </TenderTable>
        </TabsContent>

        {/* Quoted Tenders */}
        <TabsContent value="quoted">
          <TenderTable testid="quoted-table" rows={data.quoted || []} empty="No quoted tenders yet. Mark a tender 'Bid Submitted' to move it here."
            cols={["Tender", "Dept", "Value", "EMD", "Rank", "Result"]}>
            {(t) => {
              const won = t.status === "Won";
              const lost = t.status === "Lost";
              return (
                <tr key={t.id} className={`border-b border-slate-100 hover:bg-slate-50/60 ${won ? "bg-emerald-50/50" : lost ? "bg-red-50/40" : ""}`} data-testid={`quoted-row-${t.id}`}>
                  <td className="py-2.5 px-3 font-semibold text-slate-900">{t.tender_title || "—"}<div className="text-[11px] text-slate-400 font-mono">{t.tender_id || ""}</div></td>
                  <td className="py-2.5 px-3 text-slate-700">{t.department || "—"}</td>
                  <td className="py-2.5 px-3 font-mono font-semibold">{formatCurrency(t.tender_value)}</td>
                  <td className="py-2.5 px-3 font-mono">{formatCurrency(t.emd)}</td>
                  <td className="py-2.5 px-3 font-mono">{t.rank || "—"}</td>
                  <td className="py-2.5 px-3"><Badge variant="outline" className={STATUS_BADGE(t.status)}>{t.status}</Badge></td>
                  <OpenCell module="tenders_published" id={t.id} />
                </tr>
              );
            }}
          </TenderTable>
        </TabsContent>

        {/* Tenders Won */}
        <TabsContent value="won">
          <TenderTable testid="won-table" rows={data.won || []} empty="No tenders won yet. Mark a quoted tender 'Won' to celebrate it here."
            cols={["Tender", "Dept", "State", "Value", "EMD", "Assigned"]}>
            {(t) => (
              <tr key={t.id} className="border-b border-slate-100 hover:bg-slate-50/60 bg-emerald-50/40" data-testid={`won-row-${t.id}`}>
                <td className="py-2.5 px-3 font-semibold text-slate-900">{t.tender_title || "—"}<div className="text-[11px] text-slate-400 font-mono">{t.tender_id || ""}</div></td>
                <td className="py-2.5 px-3 text-slate-700">{t.department || "—"}</td>
                <td className="py-2.5 px-3 text-slate-600">{t.state || "—"}</td>
                <td className="py-2.5 px-3 font-mono font-bold text-emerald-700">{formatCurrency(t.tender_value)}</td>
                <td className="py-2.5 px-3 font-mono">{formatCurrency(t.emd)}</td>
                <td className="py-2.5 px-3 text-slate-700">{t.assigned_to || "—"}</td>
                <OpenCell module="tenders_published" id={t.id} />
              </tr>
            )}
          </TenderTable>
        </TabsContent>

        {/* EMD Tracking */}
        <TabsContent value="emd">
          <TenderTable testid="emd-table" rows={data.emd || []} empty="No EMD submissions to track. EMD auto-flags for refund once a tender is 'Bid Submitted'."
            cols={["Tender", "Dept", "EMD Amount", "Tender Result", "EMD Status"]}>
            {(t) => (
              <tr key={t.id} className={`border-b border-slate-100 hover:bg-slate-50/60 ${(t.emd_status || "") !== "Refunded" ? "bg-blue-50/30" : ""}`} data-testid={`emd-row-${t.id}`}>
                <td className="py-2.5 px-3 font-semibold text-slate-900">{t.tender_title || "—"}<div className="text-[11px] text-slate-400 font-mono">{t.tender_id || ""}</div></td>
                <td className="py-2.5 px-3 text-slate-700">{t.department || "—"}</td>
                <td className="py-2.5 px-3 font-mono font-bold">{formatCurrency(t.emd)}</td>
                <td className="py-2.5 px-3"><Badge variant="outline" className={STATUS_BADGE(t.status)}>{t.status}</Badge></td>
                <td className="py-2.5 px-3"><Badge variant="outline" className={STATUS_BADGE(t.emd_status || "")}>{t.emd_status || "To Be Submitted"}</Badge></td>
                <OpenCell module="tenders_published" id={t.id} />
              </tr>
            )}
          </TenderTable>
        </TabsContent>

        {/* Pending Tasks */}
        <TabsContent value="tasks"><Card className="border-slate-200"><CardContent className="p-5">
          <div className="flex items-center justify-between mb-3 gap-2 flex-wrap">
            <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Assigned Tasks</div>
            <div className="flex gap-2">
              <Badge variant="outline" className="border-red-200 text-red-700 bg-red-50">{s.overdue_tasks} overdue</Badge>
              <Badge variant="outline" className="border-amber-200 text-amber-700 bg-amber-50">{s.due_soon_tasks} due 7d</Badge>
            </div>
          </div>
          <TasksTable rows={data.tasks || []} />
        </CardContent></Card></TabsContent>
      </Tabs>
    </div>
  );
}
